'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as Ariakit from '@ariakit/react';
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
export function TabGroupProvider({ defaultSelectedId, onTabSelect, children, }) {
    const store = Ariakit.useTabStore({ defaultSelectedId });
    const selectedId = Ariakit.useStoreState(store, 'selectedId');
    useEffect(() => {
        if (!selectedId || !onTabSelect)
            return;
        onTabSelect(selectedId);
    }, [selectedId, onTabSelect]);
    return _jsx(Ariakit.TabProvider, { store: store, children: children });
}
export function TabGroupList({ children, className, ...rest }) {
    const [animate, setAnimate] = useState(false);
    const [indicator, setIndicator] = useState({ offsetX: 0, width: 0 });
    const listRef = useRef(null);
    const store = Ariakit.useTabContext();
    const selectedId = Ariakit.useStoreState(store, 'selectedId');
    const updateIndicator = useCallback(() => {
        const list = listRef.current;
        if (!list)
            return;
        const selected = list.querySelector('[role="tab"][aria-selected="true"]');
        if (selected)
            setIndicator({ offsetX: selected.offsetLeft, width: selected.offsetWidth });
    }, []);
    useLayoutEffect(() => {
        updateIndicator();
        const id = requestAnimationFrame(() => setAnimate(true));
        return () => cancelAnimationFrame(id);
    }, [updateIndicator, selectedId]);
    useEffect(() => {
        const onResize = updateIndicator;
        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [updateIndicator]);
    return (_jsxs(ListWrap, { className: className, children: [_jsx(TabList, { ref: listRef, ...rest, children: children }), _jsx(Indicator, { "$offsetX": indicator.offsetX, "$width": indicator.width, "$animate": animate })] }));
}
export function TabGroupTab({ children, ...rest }) {
    return (_jsx(TabButton, { render: _jsx("button", {}), ...rest, children: children }));
}
export function TabGroupPanel({ children, ...rest }) {
    return _jsx(Ariakit.TabPanel, { ...rest, children: children });
}
export function Tab(_props) {
    return null;
}
export function TabGroup({ children, onTabSelect, className }) {
    const childArray = useMemo(() => React.Children.toArray(children).filter(Boolean), [children]);
    const defaultIndex = useMemo(() => {
        const defaultTabs = childArray.filter(child => child.props.default);
        const firstDefault = childArray.findIndex(child => child.props.default);
        if (defaultTabs.length > 1 && process.env.NODE_ENV !== 'production') {
            console.warn('[TabGroup] Multiple <Tab default /> found. Using the first one at index');
        }
        return firstDefault;
    }, [childArray]);
    const tabItems = useMemo(() => {
        return childArray.map((child, i) => {
            const id = `tab-${i}`;
            const name = child.props.name ?? `Tab ${i + 1}`;
            const renderProp = child.props.render;
            const label = renderProp ?? name;
            return {
                id,
                name,
                label,
                panel: child.props.children,
            };
        });
    }, [childArray]);
    const defaultSelectedId = useMemo(() => (tabItems[defaultIndex] ? tabItems[defaultIndex].id : 'tab-0'), [tabItems, defaultIndex]);
    const handleTabSelect = useCallback((id) => {
        if (!onTabSelect)
            return;
        const current = tabItems.find(item => item.id === id);
        if (current)
            onTabSelect(current.name);
    }, [tabItems, onTabSelect]);
    return (_jsx(Group, { className: className, children: _jsxs(TabGroupProvider, { defaultSelectedId: defaultSelectedId, onTabSelect: handleTabSelect, children: [_jsx(TabGroupList, { children: tabItems.map(({ id, label }) => (_jsx(TabGroupTab, { id: id, "aria-controls": `panel-${id}`, children: label }, id))) }), tabItems.map(({ id, panel }) => (_jsx(TabGroupPanel, { tabId: id, id: `panel-${id}`, children: panel }, `panel-${id}`)))] }) }));
}
const Group = styled.div `
  padding: ${o => o.theme.spacing(4)};
`;
const ListWrap = styled.div `
  position: relative;
  border-bottom: 1px solid ${o => o.theme.color.stroke.neutral.border.decoration.on.background};
  overflow-x: auto;
  overflow-y: hidden;
`;
const TabList = styled(Ariakit.TabList) `
  display: flex;
  gap: ${o => o.theme.spacing(2)};
`;
const TabButton = styled(Ariakit.Tab) `
  position: relative;
  padding: ${o => o.theme.spacing(3)};
  margin: 0;
  font: inherit;
  color: ${o => o.theme.color.stroke.disabled.text.on.background};
  transition: color 200ms ease;
  font-weight: 700;
  text-transform: uppercase;

  &:hover,
  &[aria-selected='true'] {
    color: ${o => o.theme.color.text.primary.on.background};
  }
`;
const Indicator = styled.div `
  position: absolute;
  bottom: -1px;
  height: 4px;
  background: ${o => o.theme.color.fill.strong};
  width: ${p => p.$width}px;
  transform: translateX(${p => p.$offsetX}px);
  transition: ${p => (p.$animate ? 'transform 250ms ease, width 250ms ease' : 'none')};
  will-change: transform, width;
`;
//# sourceMappingURL=TabGroup.js.map